import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BudgetScenarioType } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../components/Loaders/Loader';
import LoadError from '../../components/Budget/LoadError';
import ConfirmationDialog from '../../components/UI/Dialogs/ConfirmationDialog';
import ScenarioMetaForm from '../../components/Budget/ScenarioMetaForm';
import FormSaveActions from '../../components/Budget/FormSaveActions';
import ScenarioSection, { ScenarioSectionView } from '../../components/Budget/ScenarioSection';
import ScenarioScopeField from '../../components/Budget/ScenarioScopeField';
import { useScenarioScope } from '../../components/Budget/useScenarioScope';
import { draftToInputsPayload, newShiftDraftRow, scenarioRowAnchorId } from '../../components/Budget/scenarioDraft';
import ScenarioContactsGrid from '../../components/Budget/ScenarioContactsGrid';
import ScenarioMinutePriceGrid from '../../components/Budget/ScenarioMinutePriceGrid';
import ScenarioShiftGrid from '../../components/Budget/ScenarioShiftGrid';
import ScenarioBudgetAdjustmentGrid from '../../components/Budget/ScenarioBudgetAdjustmentGrid';
import ScenarioDriverGrid from '../../components/Budget/ScenarioDriverGrid';
import ScenarioImpactGrid, { ScenarioImpactBadge } from '../../components/Budget/ScenarioImpactGrid';
import ScenarioBudgetImpactGrid, {
    BudgetImpactView,
    ScenarioBudgetImpactViewToggle,
} from '../../components/Budget/ScenarioBudgetImpactGrid';
import { BudgetRoutes } from '../../enums/BudgetRoutes';
import { useBudgetScenario, ScenarioQueryState } from '../../hooks/api/budgetScenarios';
import {
    useScenarioContactValues,
    useScenarioImpact,
    useScenarioImpactPreview,
} from '../../hooks/api/budgetScenarioContactValues';
import { useScenarioMinutePriceValues } from '../../hooks/api/budgetScenarioMinutePriceValues';
import { useScenarioShiftRows } from '../../hooks/api/budgetScenarioShiftRows';
import { useScenarioBudgetAdjustment } from '../../hooks/api/budgetScenarioBudgetAdjustmentValues';
import { useScenarioDriverRows } from '../../hooks/api/budgetScenarioDriverRows';
import { usePublicHolidays } from '../../hooks/api/publicHolidays';
import { useScenarioForm } from '../../hooks/useScenarioForm';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useTitleContext } from '../../store/Page/TitleContext';

// The interceptor rethrows the axios error with the server message, so the HTTP status survives.
const isNotFoundError = (error: Error | null): boolean =>
    (error as { response?: { status?: number } } | null)?.response?.status === 404;

// A section either shows its content or the shared retryable error alert.
const renderWithLoadError = (state: ScenarioQueryState, content: ReactElement) =>
    state.isError ? <LoadError error={state.error} onRetry={state.refetch} /> : content;

const BudgetScenarioDetail = () => {
    const { t } = useTranslation('budget');
    const navigate = useNavigate();
    const { onChange: changeTitle } = useTitleContext();
    const { id } = useParams<{ id: string }>();
    const scenarioId = id ? Number(id) : undefined;
    // Deliberately `canEdit`, not `isReadOnly`: scenarios stay editable on snapshot versions —
    // they are a what-if layer computed against the frozen baseline (CALL-782), unlike the
    // version-scoped grids that freeze with the snapshot.
    const { canEdit } = useBudgetContext();

    const [scenario, isScenarioLoading, scenarioState] = useBudgetScenario(scenarioId);

    // AppBar title: generic "Scenarios" (menu fallback) until the name is known.
    useEffect(() => {
        if (scenario?.name) changeTitle(scenario.name);
        return () => {
            changeTitle(undefined);
        };
    }, [changeTitle, scenario?.name]);

    const isContacts = scenario?.type === BudgetScenarioType.CONTACTS;
    const isMinutePrice = scenario?.type === BudgetScenarioType.MINUTE_PRICE;
    const isShift = scenario?.type === BudgetScenarioType.SHIFT;
    const isBudgetAdjustment = scenario?.type === BudgetScenarioType.BUDGET_ADJUSTMENT;
    const isReachabilityAdjustment = scenario?.type === BudgetScenarioType.REACHABILITY_ADJUSTMENT;

    // CALL-794: the version the pickers below must describe. It has to be the DRAFT version — the Budget
    // select stays editable and the impact preview is already priced against whatever it holds, so pickers
    // answering from the stored row let a user select an entity the previewed version cannot price and save
    // it as a 0,00 € row.
    //
    // It arrives through state, one render behind `meta`, because these queries are declared ABOVE the form
    // that owns the draft — the form is fed FROM them, so it cannot also be read before them. A pure mirror
    // of `meta.budgetVersionId` (the effect below overwrites it unconditionally), so Cancel and a 409 reload
    // revert the pickers with everything else; nothing here is a second source of truth.
    //
    // The fallback is the scenario's stored version rather than `undefined`: on the render where these
    // queries first become enabled the form has not adopted server state yet, and letting them fire without
    // a version would key them differently from the very next render — i.e. two requests per page load, one
    // of them a wasted HEMT round trip for the minute-price and shift options.
    const [draftVersionId, setDraftVersionId] = useState<number>();
    const optionsVersionId = draftVersionId ?? scenario?.budgetVersionId;

    // Only fetch the active type's inputs (passing undefined disables the query) — avoids calling the
    // other type's endpoint, notably the minute-price HEMT label lookup, for a non-matching scenario.
    const [contactData, isContactLoading, contactState] = useScenarioContactValues(
        isContacts ? scenarioId : undefined,
        optionsVersionId
    );
    const [minutePriceData, isMinutePriceLoading, minutePriceState] = useScenarioMinutePriceValues(
        isMinutePrice ? scenarioId : undefined,
        optionsVersionId
    );
    const [shiftData, isShiftLoading, shiftState] = useScenarioShiftRows(
        isShift ? scenarioId : undefined,
        optionsVersionId
    );
    const [budgetAdjustmentData, isBudgetAdjustmentLoading, budgetAdjustmentState] = useScenarioBudgetAdjustment(
        isBudgetAdjustment ? scenarioId : undefined,
        optionsVersionId
    );
    const [driverData, isDriverLoading, driverState] = useScenarioDriverRows(
        isReachabilityAdjustment ? scenarioId : undefined,
        optionsVersionId
    );
    const [impact, isImpactLoading, impactState] = useScenarioImpact(scenarioId);

    // Only the active type's query is enabled, so at most one of these ever holds data.
    const inputsData = isContacts
        ? contactData
        : isMinutePrice
          ? minutePriceData
          : isShift
            ? shiftData
            : isBudgetAdjustment
              ? budgetAdjustmentData
              : isReachabilityAdjustment
                ? driverData
                : undefined;
    // Same reasoning for the flag the scope multiselect shows its spinner from.
    const isInputsLoading =
        isContactLoading || isMinutePriceLoading || isShiftLoading || isBudgetAdjustmentLoading || isDriverLoading;

    // Only the active type's query is enabled, so at most one of these carries options. A Verschiebung is
    // scoped by Working Unit rather than by worktype, so its options come from their own list.
    const availableWorktypes = useMemo(
        () =>
            contactData?.availableWorktypes ??
            budgetAdjustmentData?.availableWorktypes ??
            driverData?.availableWorktypes ??
            [],
        [contactData, budgetAdjustmentData, driverData]
    );
    // Two types offer partner/locations, and they must be chosen by TYPE rather than by "whichever answered
    // first": a disabled query resolves to undefined, but a cached empty array from the other type would win
    // a `??` chain and leave Von/Nach permanently empty.
    const availablePartnerLocations = useMemo(
        () => (isShift ? shiftData?.availablePartnerLocations : minutePriceData?.availablePartnerLocations) ?? [],
        [isShift, minutePriceData, shiftData]
    );
    const availableWorkingUnits = useMemo(() => shiftData?.availableWorkingUnits ?? [], [shiftData]);

    // Metadata AND the active type's inputs are one draft, committed by one Save (CALL-783). The
    // per-type editors below only report changes upward; nothing here writes on its own.
    const form = useScenarioForm({ scenarioId, scenario, type: scenario?.type, inputsData });
    const { meta, setMeta, inputs, setInputs, months, isDirty, issues, canSave, isSaving, conflict } = form;

    // Feed the draft version back up to the option queries above. Unconditional in one direction: while the
    // form holds no version yet (it is '' until server state is adopted) the queries keep the stored one.
    useEffect(() => {
        if (typeof meta.budgetVersionId === 'number') setDraftVersionId(meta.budgetVersionId);
    }, [meta.budgetVersionId]);

    // The national public holidays the Trainingstage prefill subtracts besides weekends (CALL-801) — the
    // same set the backend's training allocation reads from the same table, so the day count the grid
    // prefills is the count the impact spreads over. The date arrives as a UTC-midnight timestamp; its
    // first 10 chars ARE the calendar date.
    //
    // `undefined` while the query is in flight OR failed, NOT the empty set it resolves as: an empty set
    // is a real answer ("no holidays in this window") and would prefill a day count that silently counts
    // them as working days — and a failed request hands back the same empty array with isLoading false,
    // which is why isError is checked too. The grids read undefined as "cannot answer yet" and skip the
    // prefill instead; the next touch of either date (by then loaded) fills it, and a failing request
    // leaves Trainingstage to the user rather than filling it from a calendar we could not read.
    const [allHolidays, isHolidaysLoading, isHolidaysError] = usePublicHolidays();
    const holidays = useMemo(
        () =>
            isHolidaysLoading || isHolidaysError
                ? undefined
                : new Set(
                      allHolidays
                          .filter((holiday) => holiday.national)
                          .map((holiday) => String(holiday.date).slice(0, 10))
                  ),
        [allHolidays, isHolidaysLoading, isHolidaysError]
    );

    // Which entities the scenario is about — the board's focal control, and the only way to add or remove a
    // row (CALL-783). A projection of the draft, so Cancel reverts the selection with everything else.
    const scope = useScenarioScope({
        type: scenario?.type,
        inputs,
        onChange: setInputs,
        availableWorktypes,
        availablePartnerLocations,
        availableWorkingUnits,
        scenarioStart: meta.startDate || undefined,
    });

    // Input or calculated impact — one section, one view at a time. Only one type is ever live on a page,
    // so a single piece of state covers all five sections.
    const [view, setView] = useState<ScenarioSectionView>('input');
    const [budgetImpactView, setBudgetImpactView] = useState<BudgetImpactView>('relative');

    // The impact view of a dirty, valid draft is a PREVIEW of that draft: the calc functions are pure, so
    // the server can run them without persisting anything and the figures match what Save would produce.
    // Only the impact view asks for it — the input grid is not mounted there, so no keystroke can reach it.
    //
    // Gated on the form's own `canSave` rather than on `issues` alone: a draft the PATCH would reject is a
    // draft the preview rejects too, invalid metadata (an empty or inverted period) included.
    const canPreview = view === 'impact' && canSave && inputs !== null;
    // The DRAFT's budget version travels with the inputs, not just its dates: the Budget select stays
    // editable in edit mode (only the bucket is locked), and which version a scenario is pointed at decides
    // the baseline the impact is computed against — forecast volumes are version-scoped, and a released
    // snapshot's configs are frozen and never extrapolated. Sending only the inputs made the server answer
    // from the STORED version, so switching Budget appeared to change nothing until Save disagreed.
    // `canSave` already rules out an empty version (hasScenarioMetaErrors requires one), so the guard here
    // only narrows the `number | ''` for TypeScript.
    const previewPayload = useMemo(
        () =>
            canPreview && inputs && meta.budgetVersionId !== ''
                ? {
                      budgetVersionId: meta.budgetVersionId,
                      startDate: meta.startDate,
                      endDate: meta.endDate,
                      inputs: draftToInputsPayload(inputs),
                  }
                : null,
        [canPreview, inputs, meta.budgetVersionId, meta.startDate, meta.endDate]
    );
    const [previewImpact, isPreviewLoading] = useScenarioImpactPreview(scenarioId, previewPayload, canPreview);

    // Verschiebung is the one type whose rows are NOT a bijection with the scope selection — one working
    // unit may move volume to several targets — so it needs an explicit "add". The handler lives here, with
    // the draft, because the button it drives lives in the section heading (the one row both views share);
    // the grid gets the same handler for the button inside its empty state (CALL-784).
    const addShiftRow = useCallback(() => {
        setInputs((previous) => ({
            type: BudgetScenarioType.SHIFT,
            rows: [...(previous?.type === BudgetScenarioType.SHIFT ? previous.rows : []), newShiftDraftRow('', null)],
        }));
    }, [setInputs]);

    const guard = useUnsavedChangesGuard(isDirty);
    const goBack = useCallback(() => guard.confirmNavigate(() => navigate(BudgetRoutes.SCENARIOS)), [guard, navigate]);

    // "Go to error" jumps to the row grid's alert line for the first flagged row; the minute-price grid and
    // the budget-adjustment series have no row anchors (and no rules), so there is nothing to scroll to.
    const firstIssue = issues[0];
    const goToIssue = useCallback(() => {
        if (!firstIssue?.clientKey) return;
        document.getElementById(scenarioRowAnchorId(firstIssue.clientKey))?.scrollIntoView({ block: 'center' });
    }, [firstIssue]);

    const backButton = (
        <Button variant="text" size="small" startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 1 }}>
            <span>{t('scenarioDetail.backToOverview')}</span>
        </Button>
    );

    if (isScenarioLoading) {
        return (
            <Box>
                {backButton}
                <Loader />
            </Box>
        );
    }

    // Covers an invalid id in the URL (query never fires), a 404 for a deleted/unknown scenario,
    // and any other load failure (retryable).
    if (!scenario) {
        return (
            <Box>
                {backButton}
                {scenarioState.isError && !isNotFoundError(scenarioState.error) ? (
                    <LoadError error={scenarioState.error} onRetry={scenarioState.refetch} />
                ) : (
                    <Alert severity="warning">
                        <span>{t('scenarioDetail.notFound')}</span>
                    </Alert>
                )}
            </Box>
        );
    }

    const actionProps = {
        canSave,
        isDirty,
        isSaving,
        onSave: form.save,
        onCancel: form.cancel,
        // ONE short, generic line — deliberately NOT the issue's own message (CALL-784). Both action bars
        // used to repeat it verbatim, and the grid's per-row banner said it a third time, so a single
        // untouched row shouted "Von und Nach sind erforderlich" three times before the user had typed
        // anything. The banner is the useful copy (it names the row); this line only has to say that
        // something is wrong and offer the jump to it — and it is PRINTED by the footer pair alone now,
        // the header's Save carrying it as a tooltip instead (CALL-794).
        firstIssue: isDirty && !canSave && !isSaving ? t('validation.fixBeforeSave') : undefined,
        onGoToIssue: firstIssue?.clientKey ? goToIssue : undefined,
    };
    // Everything is disabled while the PATCH is in flight: it replaces the whole input set, so an edit
    // landing mid-request would be silently overwritten by the response's refetch.
    const disabled = !canEdit || isSaving;

    // A preview that has not arrived (or failed — nothing to act on, it is a read of unsaved work) falls back
    // to the last saved impact, which the badge then says so about.
    const showsPreview = canPreview && previewImpact !== undefined;
    const isStale = isDirty && !showsPreview;
    const impactProps = {
        impact: showsPreview ? previewImpact : impact,
        isLoading: canPreview ? isPreviewLoading : isImpactLoading,
        isStale,
    };
    // The badge belongs to the section heading, which both views render, rather than to the impact grid —
    // where its 32px appeared on every toggle of a dirty draft and moved the table with it (CALL-784). One
    // instance for all five sections: only one type is ever live on a page.
    const impactBadge =
        view === 'impact' ? <ScenarioImpactBadge isPreview={showsPreview} isStale={isStale} /> : undefined;

    return (
        // A plain block, deliberately: nothing on this page is pinned to the viewport any more (CALL-784), so
        // it needs neither a viewport-height minimum nor a flex column. The flex column was also what stretched
        // the back Button to the full page width — and MUI centres a Button's content, so "ZURÜCK ZU DEN
        // SZENARIEN" sat in the middle of the page instead of at its left edge.
        <Box>
            {backButton}

            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    {/* Fetched name updates in place after load — block translation to avoid the
                        extension rewriting the text node and crashing React reconciliation. */}
                    <span className="notranslate">{scenario?.name ?? ''}</span>
                </Typography>
                <Chip size="small" label={<span>{t(`scenarioType.${scenario.type}`)}</span>} />
                {isDirty && <Chip size="small" color="warning" label={<span>{t('scenarios.unsavedChanges')}</span>} />}
                <Box sx={{ flexGrow: 1 }} />
                {canEdit && <FormSaveActions variant="header" {...actionProps} />}
            </Stack>

            {conflict.isConflicted && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Stack direction="row" spacing={1}>
                            <Button color="inherit" size="small" onClick={conflict.reloadFromServer}>
                                <span>{t('scenarioDetail.reloadFromServer')}</span>
                            </Button>
                            <Button color="inherit" size="small" onClick={conflict.keepEditing}>
                                <span>{t('scenarioDetail.keepEditing')}</span>
                            </Button>
                        </Stack>
                    }
                >
                    <span>{t('error.conflictRetry')}</span>
                </Alert>
            )}

            {/* The same fields the scenario was created with, editable in place: there is no separate
                edit screen, and a read-only role simply gets them disabled. The scope multiselect belongs
                with them — it is what the type-specific rows below are keyed to. */}
            <ScenarioMetaForm
                value={meta}
                onChange={setMeta}
                mode="edit"
                disabled={disabled}
                scopeSlot={<ScenarioScopeField scope={scope} isLoading={isInputsLoading} disabled={disabled} />}
            />

            <Box sx={{ mt: 3 }} />

            {/* The per-type branches stay here rather than behind a shared ScenarioTypeSection: each grid is
                mocked module-by-module in the page's tests, and a wrapper would bypass those assertions for
                no layout gain.

                Both of a type's notes — its formula and, where its grid has calculated columns, what those
                columns are — are passed to the section together: they end up in ONE tooltip behind ONE help
                icon (CALL-783). The derived-columns note used to be a caption inside the grid, which meant
                two blocks of prose above every table. */}
            {isContacts && (
                <ScenarioSection
                    view={view}
                    onViewChange={setView}
                    inputTitle={t('scenarioDetail.inputTitle')}
                    impactTitle={t('scenarioDetail.impactTitle')}
                    hints={[t('hint.contactsFormula')]}
                    inputHints={[t('hint.contactsDerivedColumns')]}
                    badge={impactBadge}
                >
                    {view === 'input'
                        ? renderWithLoadError(
                              contactState,
                              <ScenarioContactsGrid
                                  months={months}
                                  entities={inputs?.type === BudgetScenarioType.CONTACTS ? inputs.entities : []}
                                  // The grid's updater is passed THROUGH, not flattened: it has to be applied
                                  // to the entities the FORM holds, because one gesture can produce several of
                                  // these calls before React renders again (see DraftUpdater in scenarioDraft).
                                  onChange={(update) =>
                                      setInputs((previous) => ({
                                          type: BudgetScenarioType.CONTACTS,
                                          entities: update(
                                              previous?.type === BudgetScenarioType.CONTACTS ? previous.entities : []
                                          ),
                                      }))
                                  }
                                  issues={issues}
                                  availableWorktypes={availableWorktypes}
                                  isLoading={isContactLoading}
                                  canEdit={!disabled}
                                  holidays={holidays}
                              />
                          )
                        : renderWithLoadError(impactState, <ScenarioImpactGrid {...impactProps} />)}
                </ScenarioSection>
            )}

            {isMinutePrice && (
                <ScenarioSection
                    view={view}
                    onViewChange={setView}
                    inputTitle={t('scenarioDetail.minutePriceInputTitle')}
                    impactTitle={t('scenarioDetail.impactTitle')}
                    hints={[t('hint.minutePriceFormula')]}
                    badge={impactBadge}
                >
                    {view === 'input'
                        ? renderWithLoadError(
                              minutePriceState,
                              <ScenarioMinutePriceGrid
                                  months={months}
                                  entityIds={scope.selectedIds}
                                  draft={inputs?.type === BudgetScenarioType.MINUTE_PRICE ? inputs.entities : []}
                                  onChange={(update) =>
                                      setInputs((previous) => ({
                                          type: BudgetScenarioType.MINUTE_PRICE,
                                          entities: update(
                                              previous?.type === BudgetScenarioType.MINUTE_PRICE
                                                  ? previous.entities
                                                  : []
                                          ),
                                      }))
                                  }
                                  availablePartnerLocations={availablePartnerLocations}
                                  isLoading={isMinutePriceLoading}
                                  canEdit={!disabled}
                              />
                          )
                        : renderWithLoadError(
                              impactState,
                              <ScenarioImpactGrid {...impactProps} entityHeader={t('field.partnerLocation')} />
                          )}
                </ScenarioSection>
            )}

            {isShift && (
                <ScenarioSection
                    view={view}
                    onViewChange={setView}
                    inputTitle={t('scenarioDetail.shiftInputTitle')}
                    impactTitle={t('scenarioDetail.impactTitle')}
                    hints={[t('hint.shiftFormula')]}
                    inputHints={[t('hint.shiftDerivedColumns')]}
                    badge={impactBadge}
                    // The one type whose rows are not a bijection with the scope selection, so it needs an
                    // explicit "add". In the heading rather than in a row of its own above the grid: that
                    // row only existed in the input view, and its 44px vanished on every toggle.
                    actions={
                        view === 'input' && canEdit ? (
                            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addShiftRow}>
                                <span>{t('scenarioDetail.addShiftRow')}</span>
                            </Button>
                        ) : undefined
                    }
                >
                    {view === 'input'
                        ? renderWithLoadError(
                              shiftState,
                              <ScenarioShiftGrid
                                  months={months}
                                  rows={inputs?.type === BudgetScenarioType.SHIFT ? inputs.rows : []}
                                  onChange={(update) =>
                                      setInputs((previous) => ({
                                          type: BudgetScenarioType.SHIFT,
                                          rows: update(
                                              previous?.type === BudgetScenarioType.SHIFT ? previous.rows : []
                                          ),
                                      }))
                                  }
                                  issues={issues}
                                  availablePartnerLocations={availablePartnerLocations}
                                  availableWorkingUnits={availableWorkingUnits}
                                  onAddRow={addShiftRow}
                                  isLoading={isShiftLoading}
                                  canEdit={!disabled}
                                  holidays={holidays}
                              />
                          )
                        : renderWithLoadError(
                              impactState,
                              <ScenarioImpactGrid {...impactProps} entityHeader={t('field.shiftRoute')} />
                          )}
                </ScenarioSection>
            )}

            {isBudgetAdjustment && (
                <ScenarioSection
                    view={view}
                    onViewChange={setView}
                    inputTitle={t('scenarioDetail.budgetAdjustmentInputTitle')}
                    // The same "Berechnete Auswirkung" as the other four types (CALL-784). It used to read
                    // "Auswirkung auf Kontakte" here, which is what the table shows but made this one type's
                    // toggle read differently from every other — and the Relativ/Absolut control beside it
                    // already says which contacts metric is on screen.
                    impactTitle={t('scenarioDetail.impactTitle')}
                    hints={[t('hint.budgetAdjustmentFormula')]}
                    badge={impactBadge}
                    actions={
                        view === 'impact' ? (
                            <ScenarioBudgetImpactViewToggle value={budgetImpactView} onChange={setBudgetImpactView} />
                        ) : undefined
                    }
                >
                    {view === 'input'
                        ? renderWithLoadError(
                              budgetAdjustmentState,
                              <ScenarioBudgetAdjustmentGrid
                                  months={months}
                                  monthValues={
                                      inputs?.type === BudgetScenarioType.BUDGET_ADJUSTMENT ? inputs.monthValues : {}
                                  }
                                  // BOTH halves come from `previous`: the euro series the updater edits AND the
                                  // worktype list beside it. Rebuilding that list from the render-time `inputs`
                                  // would be the same staleness one level down — the conversion scope reset to
                                  // whatever the last render happened to hold.
                                  onChange={(update) =>
                                      setInputs((previous) => {
                                          const current =
                                              previous?.type === BudgetScenarioType.BUDGET_ADJUSTMENT
                                                  ? previous
                                                  : undefined;
                                          return {
                                              type: BudgetScenarioType.BUDGET_ADJUSTMENT,
                                              monthValues: update(current?.monthValues ?? {}),
                                              worktypeIds: current?.worktypeIds ?? [],
                                          };
                                      })
                                  }
                                  isLoading={isBudgetAdjustmentLoading}
                                  canEdit={!disabled}
                              />
                          )
                        : renderWithLoadError(
                              impactState,
                              // The worktype list that scopes the € → contacts conversion is NOT here any
                              // more: below the very table it filters it was both unreachable from the
                              // input tab and drawn under that table. It sits with every other type's, in
                              // the metadata form above the section heading.
                              <ScenarioBudgetImpactGrid {...impactProps} view={budgetImpactView} />
                          )}
                </ScenarioSection>
            )}

            {isReachabilityAdjustment && (
                <ScenarioSection
                    view={view}
                    onViewChange={setView}
                    inputTitle={t('scenarioDetail.driverInputTitle')}
                    impactTitle={t('scenarioDetail.impactTitle')}
                    hints={[t('hint.driverFormula')]}
                    inputHints={[t('hint.driverDerivedColumns')]}
                    badge={impactBadge}
                >
                    {view === 'input'
                        ? renderWithLoadError(
                              driverState,
                              <ScenarioDriverGrid
                                  months={months}
                                  rows={inputs?.type === BudgetScenarioType.REACHABILITY_ADJUSTMENT ? inputs.rows : []}
                                  onChange={(update) =>
                                      setInputs((previous) => ({
                                          type: BudgetScenarioType.REACHABILITY_ADJUSTMENT,
                                          rows: update(
                                              previous?.type === BudgetScenarioType.REACHABILITY_ADJUSTMENT
                                                  ? previous.rows
                                                  : []
                                          ),
                                      }))
                                  }
                                  issues={issues}
                                  availableWorktypes={availableWorktypes}
                                  isLoading={isDriverLoading}
                                  canEdit={!disabled}
                                  holidays={holidays}
                              />
                          )
                        : renderWithLoadError(impactState, <ScenarioImpactGrid {...impactProps} />)}
                </ScenarioSection>
            )}

            {scenario &&
                !isContacts &&
                !isMinutePrice &&
                !isShift &&
                !isBudgetAdjustment &&
                !isReachabilityAdjustment && (
                    <Typography variant="body2" color="text.secondary">
                        {t('scenarioDetail.typeNotYetSupported')}
                    </Typography>
                )}

            {/* The second Save/Cancel pair, an ordinary row after the content rather than a bar pinned to
                the bottom of the viewport (CALL-784): a floating bar on a wide screen does not read as part
                of anything, and everything it needed to work — reserved height so the last grid row could
                be scrolled clear of it, an opaque background, a shadow, a z-index grid popups had to be
                lifted above — is gone with it. Left-aligned, under the table it commits. */}
            {canEdit && <FormSaveActions variant="footer" {...actionProps} />}

            <ConfirmationDialog
                open={guard.isConfirmOpen}
                title={t('dialog.unsavedChangesTitle')}
                text={t('dialog.unsavedChangesBody')}
                onClose={guard.cancelLeave}
                onConfirm={guard.confirmLeave}
            />
        </Box>
    );
};

export default BudgetScenarioDetail;
